'use client';

import React, { createContext, ReactNode, useContext, useMemo } from 'react';

import Image from 'next/image';

import { PaymentType } from '@/helpers/constants/shopping';
import cn from '@/helpers/utils/classNames';
import { roundToTwoDecimalPlaces } from '@/helpers/utils/numberHelpers';
import { AddToCartToast } from '@/types/Cart';

type CartItemProps = {
  product?: AddToCartToast;
  children: ReactNode;
};

const CartItemContext = createContext<AddToCartToast | undefined>(undefined);
const useCartItemContext = () => {
  const product = useContext(CartItemContext) || ({} as AddToCartToast);

  if (!product) throw new Error('CartItem.* can only be called as childs of CartItem component.');

  return {
    product,
  };
};

const ImageDisplay: React.FC = () => {
  const { product } = useCartItemContext();
  return (
    <div className="v2-relative v2-aspect-square v2-h-[108px] v2-w-[108px] v2-rounded-md v2-bg-grayscale-5">
      <div className="v2-absolute v2-inset-1 v2-z-50">
        <div className="v2-relative v2-h-full v2-w-full">
          <Image src={product.image} alt={product.name} style={{ objectFit: 'contain' }} fill={true} />
        </div>
      </div>
    </div>
  );
};

const ProductInfo: React.FC = () => {
  const { product } = useCartItemContext();

  const price = useMemo(() => {
    switch (product.paymentType) {
      case PaymentType.CREDIT_CARD:
        return `$${roundToTwoDecimalPlaces(product.price ?? 0)}`;
      case PaymentType.PAW_POINT:
        return `${product?.pawPoints} PP`;
      case PaymentType.SAMPLE_CREDIT:
        return `${product?.sampleCredits} SC`;
      default:
        return `$${roundToTwoDecimalPlaces(product.price ?? 0)}`;
    }
  }, [product]);

  return (
    <div className="v2-text-gray-600 v2-text-sm sm:v2-text-base v2-font-light">
      <p>
        <b className="v2-font-semibold">Weight: </b>
        <span className="v2-font-light">{`${roundToTwoDecimalPlaces(product.weight)} lb`}</span>
      </p>
      <p>
        <b className="v2-font-semibold">Quantity: </b>
        <span className="v2-font-light">{product.quantity}</span>
      </p>
      <p>
        <span className="v2-font-semibold">{price}</span>
      </p>
    </div>
  );
};

const CartItem = ({ product, children }: CartItemProps) => {
  return (
    <CartItemContext.Provider value={product}>
      <div className={cn('flex flex-row gap-3')}>{children}</div>
    </CartItemContext.Provider>
  );
};

CartItem.ProductInfo = ProductInfo;
CartItem.ImageDisplay = ImageDisplay;

export default CartItem;
